'use client';

import { Divider, Stack, Typography } from '@mui/material';
import { ResourceType } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { ResourceTypeIndicator } from '@/components/cloudProviderIndicator';
import { MainDialog } from '@/components/dialogs/mainDialog';
import { Link } from '@/components/shared/link';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { getDocsUrl } from '@/docs/docsUrls';
import { pluralize } from '@/utils/string';

export const highFrequencyPolicyResourceTypeLimitations: ResourceTypeLimitations =
  {
    [ResourceType.AwsEc2]: {
      docsUrl: getDocsUrl('ec2EncryptedVolumeAccess'),
    },
  };

type ResourceTypeLimitations = Partial<
  Record<ResourceType, { docsUrl: string; ff?: string }>
>;

export const SupportedResourceTypesText = ({
  resourceTypes,
  resourceTypeLimitations,
}: {
  resourceTypes?: ResourceType[];
  resourceTypeLimitations?: ResourceTypeLimitations;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Typography
        component='span'
        className='underline cursor-pointer'
        onClick={() => setIsOpen(true)}
      >
        {resourceTypes?.length}{' '}
        {pluralize('resource type', resourceTypes?.length)}
      </Typography>
      <MainDialog
        isOpen={isOpen}
        header={{
          hideBorder: true,
          title: 'Available resource types',
          onClose: () => setIsOpen(false),
        }}
        isLoading={false}
        sx={{ maxHeight: '60vh' }}
      >
        <Stack
          direction='column'
          divider={<Divider sx={{ borderStyle: 'dashed' }} />}
          sx={{
            overflowY: 'auto',
            gap: '12px',
          }}
        >
          {resourceTypes?.map((resourceType) => {
            return (
              <ResourceTypeRow
                key={resourceType}
                resourceType={resourceType}
                resourceTypeLimitations={resourceTypeLimitations}
              />
            );
          })}
        </Stack>
      </MainDialog>
    </>
  );
};

const ResourceTypeRow = ({
  resourceType,
  resourceTypeLimitations,
}: {
  resourceType: ResourceType;
  resourceTypeLimitations?: ResourceTypeLimitations;
}) => {
  const resourceTypeConfig = resourceTypeLimitations?.[resourceType];
  const docsUrl = resourceTypeConfig?.docsUrl;
  const allFlags = useFeatureFlags();
  const isLimited = resourceTypeConfig?.ff
    ? allFlags[resourceTypeConfig.ff]
    : true;

  return (
    <Stack
      key={resourceType}
      sx={{ minHeight: '32px', justifyContent: 'center', paddingX: '40px' }}
    >
      <Stack
        direction='row'
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <ResourceTypeIndicator resourceType={resourceType} />
        {isLimited && docsUrl && (
          <Stack direction='row' sx={{ gap: '24px', alignItems: 'center' }}>
            <Typography>Some limitations apply</Typography>
            <ButtonWithIcon
              icon='material-symbols-book-ribbon-outline-rounded'
              component={Link}
              href={docsUrl}
              target='_blank'
              rel='noopener noreferrer'
              text='View'
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export const AvailableForText = ({
  resourceTypes,
  resourceTypeLimitations,
}: {
  resourceTypes: ResourceType[];
  resourceTypeLimitations?: ResourceTypeLimitations;
}) => {
  return (
    <Stack
      direction='row'
      sx={{
        gap: '4px',
        alignItems: 'center',
      }}
    >
      <Typography
        align='center'
        sx={{
          padding: '12px 0px',
        }}
      >
        Available for
      </Typography>
      <SupportedResourceTypesText
        resourceTypes={resourceTypes}
        resourceTypeLimitations={resourceTypeLimitations}
      />
    </Stack>
  );
};
